import { nutritionApi } from './nutritionApi';
import type { NutritionWorkspaceMutationOperation } from './nutritionApi';
import type {
  NutritionDayEntry,
  NutritionEntriesByDate,
  NutritionFoodDatabaseEntry,
  NutritionFoodEntry,
  NutritionMetricTotals,
  NutritionWorkspaceData,
} from '../types/nutrition';

export type {
  NutritionDayEntry,
  NutritionEntriesByDate,
  NutritionFoodDatabaseEntry,
  NutritionFoodEntry,
  NutritionMetricKey,
  NutritionMetricTotals,
  NutritionWorkspaceData,
} from '../types/nutrition';

export const DEFAULT_NUTRITION_TARGETS: NutritionMetricTotals = {
  calories: 2200,
  carbs: 220,
  protein: 170,
  fat: 70,
};

const EMPTY_TOTALS: NutritionMetricTotals = {
  calories: 0,
  carbs: 0,
  protein: 0,
  fat: 0,
};

const cloneWorkspace = (workspace: NutritionWorkspaceData): NutritionWorkspaceData =>
  JSON.parse(JSON.stringify(workspace)) as NutritionWorkspaceData;

let persistedWorkspaceSnapshot: NutritionWorkspaceData | null = null;

const parseMetricValue = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : fallback;

const parseServingCountValue = (value: unknown, fallback = 1): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;

const normalizeServingUnit = (value: unknown): string => {
  if (typeof value !== 'string') {
    return 'serving';
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : 'serving';
};

const parseUpdatedAt = (value: unknown): string => {
  if (typeof value !== 'string') {
    return new Date().toISOString();
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? new Date().toISOString() : new Date(timestamp).toISOString();
};

const createId = (prefix: string): string =>
  `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;

export const createEmptyNutritionFood = (
  overrides: Partial<Omit<NutritionFoodEntry, 'id'>> = {},
): NutritionFoodEntry => ({
  id: createId('nutrition-food'),
  name: typeof overrides.name === 'string' ? overrides.name : '',
  note: typeof overrides.note === 'string' ? overrides.note : '',
  servingCount: parseServingCountValue(overrides.servingCount),
  servingUnit: normalizeServingUnit(overrides.servingUnit),
  calories: parseMetricValue(overrides.calories),
  carbs: parseMetricValue(overrides.carbs),
  protein: parseMetricValue(overrides.protein),
  fat: parseMetricValue(overrides.fat),
});

export const createNutritionFoodDatabaseEntry = (
  overrides: Partial<Omit<NutritionFoodDatabaseEntry, 'id' | 'updatedAt'>> = {},
): NutritionFoodDatabaseEntry => ({
  id: createId('nutrition-food-db'),
  name: typeof overrides.name === 'string' ? overrides.name : '',
  servingCount: parseServingCountValue(overrides.servingCount),
  servingUnit: normalizeServingUnit(overrides.servingUnit),
  calories: parseMetricValue(overrides.calories),
  carbs: parseMetricValue(overrides.carbs),
  protein: parseMetricValue(overrides.protein),
  fat: parseMetricValue(overrides.fat),
  updatedAt: new Date().toISOString(),
});

export const createEmptyNutritionDay = (
  dateKey: string,
  targets: NutritionMetricTotals = DEFAULT_NUTRITION_TARGETS,
): NutritionDayEntry => ({
  dateKey,
  targets: { ...targets },
  foods: [],
  updatedAt: new Date().toISOString(),
});

export const getDefaultNutritionWorkspace = (): NutritionWorkspaceData => ({
  version: 0,
  entries: {},
  foodDatabase: [],
});

const parseVersionValue = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;

const normalizeTargets = (targets: unknown): NutritionMetricTotals => {
  if (!targets || typeof targets !== 'object') {
    return { ...DEFAULT_NUTRITION_TARGETS };
  }

  const candidate = targets as Partial<NutritionMetricTotals>;

  return {
    calories: parseMetricValue(candidate.calories, DEFAULT_NUTRITION_TARGETS.calories),
    carbs: parseMetricValue(candidate.carbs, DEFAULT_NUTRITION_TARGETS.carbs),
    protein: parseMetricValue(candidate.protein, DEFAULT_NUTRITION_TARGETS.protein),
    fat: parseMetricValue(candidate.fat, DEFAULT_NUTRITION_TARGETS.fat),
  };
};

const normalizeFood = (food: unknown): NutritionFoodEntry => {
  if (!food || typeof food !== 'object') {
    return createEmptyNutritionFood();
  }

  const candidate = food as Partial<NutritionFoodEntry>;

  return {
    id: typeof candidate.id === 'string' && candidate.id.length > 0 ? candidate.id : createId('nutrition-food'),
    name: typeof candidate.name === 'string' ? candidate.name : '',
    note: typeof candidate.note === 'string' ? candidate.note : '',
    servingCount: parseServingCountValue(candidate.servingCount),
    servingUnit: normalizeServingUnit(candidate.servingUnit),
    calories: parseMetricValue(candidate.calories),
    carbs: parseMetricValue(candidate.carbs),
    protein: parseMetricValue(candidate.protein),
    fat: parseMetricValue(candidate.fat),
  };
};

const normalizeFoodDatabaseEntry = (food: unknown): NutritionFoodDatabaseEntry | null => {
  if (!food || typeof food !== 'object') {
    return null;
  }

  const candidate = food as Partial<NutritionFoodDatabaseEntry>;
  const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';

  if (name.length === 0) {
    return null;
  }

  return {
    id: typeof candidate.id === 'string' && candidate.id.length > 0 ? candidate.id : createId('nutrition-food-db'),
    name,
    servingCount: parseServingCountValue(candidate.servingCount),
    servingUnit: normalizeServingUnit(candidate.servingUnit),
    calories: parseMetricValue(candidate.calories),
    carbs: parseMetricValue(candidate.carbs),
    protein: parseMetricValue(candidate.protein),
    fat: parseMetricValue(candidate.fat),
    updatedAt: parseUpdatedAt(candidate.updatedAt),
  };
};

const normalizeDay = (dateKey: string, entry: unknown): NutritionDayEntry => {
  if (!entry || typeof entry !== 'object') {
    return createEmptyNutritionDay(dateKey);
  }

  const candidate = entry as Partial<NutritionDayEntry>;

  return {
    dateKey,
    targets: normalizeTargets(candidate.targets),
    foods: Array.isArray(candidate.foods) ? candidate.foods.map((food) => normalizeFood(food)) : [],
    updatedAt: parseUpdatedAt(candidate.updatedAt),
  };
};

const isWorkspaceData = (value: unknown): value is NutritionWorkspaceData => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<NutritionWorkspaceData>;

  return candidate.entries !== undefined
    && candidate.entries !== null
    && typeof candidate.entries === 'object'
    && !Array.isArray(candidate.entries)
    && Array.isArray(candidate.foodDatabase);
};

export const normalizeNutritionWorkspace = (workspace: NutritionWorkspaceData): NutritionWorkspaceData => {
  const entries = Object.entries(workspace.entries).reduce<NutritionEntriesByDate>((normalized, [dateKey, entry]) => {
    normalized[dateKey] = normalizeDay(dateKey, entry);
    return normalized;
  }, {});

  const foodDatabase = workspace.foodDatabase
    .map((food) => normalizeFoodDatabaseEntry(food))
    .filter((food): food is NutritionFoodDatabaseEntry => food !== null)
    .sort((left, right) => left.name.localeCompare(right.name));

  return {
    version: parseVersionValue(workspace.version),
    entries,
    foodDatabase,
  };
};

export const getNutritionTargetsForDate = (
  entries: NutritionEntriesByDate,
  dateKey: string,
): NutritionMetricTotals => {
  const latestDateKey = Object.keys(entries)
    .filter((entryDateKey) => entryDateKey <= dateKey)
    .sort((left, right) => right.localeCompare(left))[0];

  if (!latestDateKey) {
    return { ...DEFAULT_NUTRITION_TARGETS };
  }

  return { ...entries[latestDateKey]!.targets };
};

export const calculateNutritionFoodTotals = (food: NutritionFoodEntry): NutritionMetricTotals => ({
  calories: food.calories * food.servingCount,
  carbs: food.carbs * food.servingCount,
  protein: food.protein * food.servingCount,
  fat: food.fat * food.servingCount,
});

export const summarizeNutritionFoods = (foods: NutritionFoodEntry[]): NutritionMetricTotals =>
  foods.reduce<NutritionMetricTotals>(
    (totals, food) => {
      const foodTotals = calculateNutritionFoodTotals(food);

      return {
        calories: totals.calories + foodTotals.calories,
        carbs: totals.carbs + foodTotals.carbs,
        protein: totals.protein + foodTotals.protein,
        fat: totals.fat + foodTotals.fat,
      };
    },
    { ...EMPTY_TOTALS },
  );

const setPersistedWorkspaceSnapshot = (workspace: NutritionWorkspaceData): NutritionWorkspaceData => {
  persistedWorkspaceSnapshot = normalizeNutritionWorkspace(cloneWorkspace(workspace));
  return persistedWorkspaceSnapshot;
};

const areMetricTotalsEqual = (
  left: NutritionMetricTotals,
  right: NutritionMetricTotals,
): boolean => (
  left.calories === right.calories
  && left.carbs === right.carbs
  && left.protein === right.protein
  && left.fat === right.fat
);

const areFoodsEqual = (left: NutritionFoodEntry, right: NutritionFoodEntry): boolean => (
  left.id === right.id
  && left.name === right.name
  && left.note === right.note
  && left.servingCount === right.servingCount
  && left.servingUnit === right.servingUnit
  && left.calories === right.calories
  && left.carbs === right.carbs
  && left.protein === right.protein
  && left.fat === right.fat
);

const areSavedFoodsEqual = (
  left: NutritionFoodDatabaseEntry,
  right: NutritionFoodDatabaseEntry,
): boolean => (
  left.id === right.id
  && left.name === right.name
  && left.servingCount === right.servingCount
  && left.servingUnit === right.servingUnit
  && left.calories === right.calories
  && left.carbs === right.carbs
  && left.protein === right.protein
  && left.fat === right.fat
  && left.updatedAt === right.updatedAt
);

const buildNutritionMutationPlan = (
  previous: NutritionWorkspaceData,
  next: NutritionWorkspaceData,
) : NutritionWorkspaceMutationOperation[] => {
  const deleteDays: NutritionWorkspaceMutationOperation[] = [];
  const deleteFoods: NutritionWorkspaceMutationOperation[] = [];
  const deleteSavedFoods: NutritionWorkspaceMutationOperation[] = [];
  const upsertDays: NutritionWorkspaceMutationOperation[] = [];
  const updateFoods: NutritionWorkspaceMutationOperation[] = [];
  const createFoods: NutritionWorkspaceMutationOperation[] = [];
  const updateSavedFoods: NutritionWorkspaceMutationOperation[] = [];
  const createSavedFoods: NutritionWorkspaceMutationOperation[] = [];

  Object.keys(previous.entries)
    .filter((dateKey) => next.entries[dateKey] === undefined)
    .sort((left, right) => left.localeCompare(right))
    .forEach((dateKey) => {
      deleteDays.push({ kind: 'deleteDay', dateKey });
    });

  Object.keys(next.entries)
    .sort((left, right) => left.localeCompare(right))
    .forEach((dateKey) => {
      const previousEntry = previous.entries[dateKey];
      const nextEntry = next.entries[dateKey];

      if (!nextEntry) {
        return;
      }

      if (
        !previousEntry
        || !areMetricTotalsEqual(previousEntry.targets, nextEntry.targets)
        || previousEntry.updatedAt !== nextEntry.updatedAt
      ) {
        upsertDays.push({
          kind: 'upsertDay',
          dateKey,
          entry: {
            targets: nextEntry.targets,
            updatedAt: nextEntry.updatedAt,
          },
        });
      }

      const previousFoods = previousEntry?.foods ?? [];
      const previousFoodsById = new Map(previousFoods.map((food) => [food.id, food] as const));
      const nextFoodsById = new Map(nextEntry.foods.map((food) => [food.id, food] as const));

      previousFoods.forEach((food) => {
        if (!nextFoodsById.has(food.id)) {
          deleteFoods.push({ kind: 'deleteFood', foodId: food.id });
        }
      });

      nextEntry.foods.forEach((food) => {
        const previousFood = previousFoodsById.get(food.id);

        if (!previousFood) {
          createFoods.push({ kind: 'createFood', dateKey, food });
          return;
        }

        if (!areFoodsEqual(previousFood, food)) {
          updateFoods.push({ kind: 'updateFood', foodId: food.id, food });
        }
      });
    });

  const previousSavedFoodsById = new Map(previous.foodDatabase.map((food) => [food.id, food] as const));
  const nextSavedFoodsById = new Map(next.foodDatabase.map((food) => [food.id, food] as const));

  previous.foodDatabase.forEach((food) => {
    if (!nextSavedFoodsById.has(food.id)) {
      deleteSavedFoods.push({ kind: 'deleteSavedFood', foodId: food.id });
    }
  });

  next.foodDatabase.forEach((food) => {
    const previousFood = previousSavedFoodsById.get(food.id);

    if (!previousFood) {
      createSavedFoods.push({ kind: 'createSavedFood', food });
      return;
    }

    if (!areSavedFoodsEqual(previousFood, food)) {
      updateSavedFoods.push({ kind: 'updateSavedFood', foodId: food.id, food });
    }
  });

  return [
    ...deleteDays,
    ...deleteFoods,
    ...deleteSavedFoods,
    ...upsertDays,
    ...updateFoods,
    ...createFoods,
    ...updateSavedFoods,
    ...createSavedFoods,
  ];
};

export const loadNutritionWorkspace = async (): Promise<NutritionWorkspaceData> => {
  const workspace = await nutritionApi.loadWorkspace();

  if (!isWorkspaceData(workspace)) {
    return cloneWorkspace(setPersistedWorkspaceSnapshot(getDefaultNutritionWorkspace()));
  }

  return cloneWorkspace(setPersistedWorkspaceSnapshot(workspace));
};

export const saveNutritionWorkspace = async (
  workspace: NutritionWorkspaceData,
): Promise<NutritionWorkspaceData> => {
  const normalizedWorkspace = normalizeNutritionWorkspace(cloneWorkspace(workspace));
  const currentPersistedWorkspace = persistedWorkspaceSnapshot;

  if (!currentPersistedWorkspace || currentPersistedWorkspace.version !== normalizedWorkspace.version) {
    const savedWorkspace = await nutritionApi.replaceWorkspace(normalizedWorkspace);

    if (!isWorkspaceData(savedWorkspace)) {
      return cloneWorkspace(setPersistedWorkspaceSnapshot(normalizedWorkspace));
    }

    return cloneWorkspace(setPersistedWorkspaceSnapshot(savedWorkspace));
  }

  const mutationPlan = buildNutritionMutationPlan(currentPersistedWorkspace, normalizedWorkspace);

  if (mutationPlan.length === 0) {
    return cloneWorkspace(currentPersistedWorkspace);
  }

  const savedWorkspace = await nutritionApi.applyWorkspaceMutations({
    version: currentPersistedWorkspace.version,
    operations: mutationPlan,
  });

  if (!isWorkspaceData(savedWorkspace)) {
    return cloneWorkspace(setPersistedWorkspaceSnapshot({
      ...normalizedWorkspace,
      version: currentPersistedWorkspace.version + 1,
    }));
  }

  return cloneWorkspace(setPersistedWorkspaceSnapshot(savedWorkspace));
};

export const nutritionStore = {
  load: loadNutritionWorkspace,
  save: saveNutritionWorkspace,
  getDefault: getDefaultNutritionWorkspace,
};

export default nutritionStore;
